import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { validateQR, getNearbyRestaurants } from '../services/api';
import { trackEvent, hashForAnalytics } from '../utils/analytics';
import { useSession } from '../context/SessionContext';
import { useCustomDomainContext } from '../context/CustomDomainContext';
import { useGeolocation } from '../hooks/useGeolocation';
import LanguageSelector from '../components/LanguageSelector';
import './ScanPage.css';

/**
 * Format distance for display
 */
function formatDistance(km) {
  if (km === null || km === undefined) return null;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

/**
 * Normalize day hours to array of ranges (supports old {open,close} and new [{open,close}])
 */
function getDayRanges(businessHours, timezone) {
  if (!businessHours) return null;
  try {
    const now = new Date();
    const options = { timeZone: timezone || 'America/Argentina/Buenos_Aires' };
    const localTime = new Date(now.toLocaleString('en-US', options));
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = dayNames[localTime.getDay()];
    const val = businessHours[today];
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (val.open) return [val];
    return [];
  } catch {
    return null;
  }
}

/**
 * Check if a restaurant is open now based on business hours and timezone
 */
function isOpenNow(businessHours, timezone) {
  const ranges = getDayRanges(businessHours, timezone);
  if (ranges === null) return null;
  if (ranges.length === 0) return false;
  try {
    const now = new Date();
    const options = { timeZone: timezone || 'America/Argentina/Buenos_Aires' };
    const localTime = new Date(now.toLocaleString('en-US', options));
    const currentTime = `${String(localTime.getHours()).padStart(2, '0')}:${String(localTime.getMinutes()).padStart(2, '0')}`;
    return ranges.some((r) => currentTime >= r.open && currentTime <= r.close);
  } catch {
    return null;
  }
}

/**
 * Get today's hours string (all ranges)
 */
function getTodayHours(businessHours, timezone) {
  const ranges = getDayRanges(businessHours, timezone);
  if (!ranges || ranges.length === 0) return null;
  return ranges.map((r) => `${r.open} - ${r.close}`).join(' | ');
}

function ScanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startSession } = useSession();
  const customDomain = useCustomDomainContext();
  const geo = useGeolocation();

  const [qrCode, setQrCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);

  // Fetch nearby restaurants when we have organizationId
  useEffect(() => {
    if (!customDomain.organizationId || customDomain.loading) return;

    setLoadingRestaurants(true);
    getNearbyRestaurants(customDomain.organizationId, geo.lat, geo.lng)
      .then((data) => setRestaurants(data.restaurants || []))
      .catch((err) => console.error('Error fetching restaurants:', err))
      .finally(() => setLoadingRestaurants(false));
  }, [customDomain.organizationId, customDomain.loading, geo.lat, geo.lng]);

  // Also re-fetch when geo finishes loading (may get distance data)
  useEffect(() => {
    if (!customDomain.organizationId || geo.loading || !geo.lat) return;

    getNearbyRestaurants(customDomain.organizationId, geo.lat, geo.lng)
      .then((data) => setRestaurants(data.restaurants || []))
      .catch((err) => console.error('Error re-fetching with location:', err));
  }, [customDomain.organizationId, geo.loading, geo.lat, geo.lng]);

  // Custom domain still loading
  if (customDomain.loading) return null;

  const handleQRSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await validateQR(qrCode);

      if (response.valid) {
        const sessionData = {
          restaurantId: response.restaurantId,
          tableNumber: response.tableNumber,
          sessionId: response.sessionId,
        };
        startSession(sessionData);
        trackEvent('qr_scanned', {
          restaurant_id: response.restaurantId,
          table_number: response.tableNumber,
          session_id: hashForAnalytics(response.sessionId),
        });
        navigate(`/r/${response.restaurantId}/t/${response.tableNumber}`);
      } else {
        setError(t('scan.invalidCode'));
      }
    } catch (err) {
      setError(t('common.error'));
      console.error('Error validating QR', err);
    } finally {
      setLoading(false);
    }
  };

  // Organization custom domain: show restaurant discovery
  if (customDomain.isCustomDomain && customDomain.organizationId) {
    return (
      <div className="scan-page scan-page--discovery">
        <div className="discovery-header">
          <h1>{t('home.title', 'Nuestros locales')}</h1>
          {geo.lat && <p className="discovery-subtitle">{t('home.nearYou', 'Cerca de ti')}</p>}
          {!geo.lat && !geo.loading && (
            <p className="discovery-subtitle discovery-subtitle--hint">
              {t('home.noLocation', 'Activá tu ubicación para ver los locales más cercanos')}
            </p>
          )}
          <LanguageSelector />
        </div>

        {loadingRestaurants ? (
          <div className="discovery-loading">
            <span className="material-icons spinning" aria-hidden="true">restaurant</span>
            <p>{t('home.loading', 'Buscando locales...')}</p>
          </div>
        ) : restaurants.length === 0 ? (
          <div className="discovery-empty">
            <span className="material-icons" aria-hidden="true">store_mall_directory</span>
            <p>{t('home.noRestaurants', 'No hay locales disponibles')}</p>
          </div>
        ) : (
          <div className="restaurant-list">
            {restaurants.map((r) => {
              const open = isOpenNow(r.businessHours, r.timezone);
              const todayHours = getTodayHours(r.businessHours, r.timezone);
              const dist = formatDistance(r.distance);

              return (
                <div
                  key={r.restaurantId}
                  className="restaurant-card"
                  onClick={() => navigate(`/r/${r.restaurantId}/takeaway`)}
                >
                  <div className="restaurant-card__logo">
                    {r.logoUrl ? (
                      <img src={r.logoUrl} alt={r.name} />
                    ) : (
                      <span className="material-icons" aria-hidden="true">restaurant</span>
                    )}
                  </div>
                  <div className="restaurant-card__info">
                    <div className="restaurant-card__header">
                      <h2>{r.name}</h2>
                      {open !== null && (
                        <span className={`restaurant-card__badge ${open ? 'open' : 'closed'}`}>
                          {open ? t('home.openNow', 'Abierto') : t('home.closed', 'Cerrado')}
                        </span>
                      )}
                    </div>
                    {r.address && <p className="restaurant-card__address">{r.address}</p>}
                    <div className="restaurant-card__meta">
                      {dist && (
                        <span className="restaurant-card__distance">
                          <span className="material-icons" aria-hidden="true">near_me</span>
                          {dist}
                        </span>
                      )}
                      {todayHours && (
                        <span className="restaurant-card__hours">
                          <span className="material-icons" aria-hidden="true">schedule</span>
                          {todayHours}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="material-icons restaurant-card__arrow" aria-hidden="true">chevron_right</span>
                </div>
              );
            })}
          </div>
        )}

        {/* QR scan section */}
        <div className="discovery-qr-section">
          <p className="discovery-qr-label">{t('home.orScanQR', 'O escaneá un código QR')}</p>
          <form onSubmit={handleQRSubmit} className="scan-form scan-form--inline">
            <input
              type="text"
              placeholder={t('scan.enterCode')}
              value={qrCode}
              onChange={(e) => setQrCode(e.target.value)}
              disabled={loading}
              className="qr-input"
            />
            <button type="submit" disabled={loading || !qrCode} className="btn btn-primary">
              <span className="material-icons" aria-hidden="true">qr_code_scanner</span>
            </button>
          </form>
          {error && <div className="error">{error}</div>}
        </div>
      </div>
    );
  }

  // Default: QR scan page (no custom domain)
  return (
    <div className="scan-page">
      <div className="scan-container">
        <h1>{t('scan.title')}</h1>
        <p className="subtitle">{t('scan.instruction')}</p>

        <LanguageSelector />

        <form onSubmit={handleQRSubmit} className="scan-form">
          <input
            type="text"
            placeholder={t('scan.enterCode')}
            value={qrCode}
            onChange={(e) => setQrCode(e.target.value)}
            disabled={loading}
            className="qr-input"
          />
          {error && <div className="error">{error}</div>}
          <button type="submit" disabled={loading || !qrCode} className="btn btn-primary">
            {loading ? t('common.loading') : t('scan.scanButton')}
          </button>
        </form>

        <div className="help-text">
          <p>{t('scan.manualEntry')}</p>
        </div>
      </div>
    </div>
  );
}

export default ScanPage;
